'use client'

import { Button, Image, Spacer, Text } from '@vinted/web-ui'

import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useAsset } from '@marketplace-web/shared/assets'
import { HelpCenterFaqEntryUrl } from '@marketplace-web/help-center/help-center-faq-entry-url-feature'
import { AccessChannel } from '@marketplace-web/help-center/help-center-faq-entry-url-data'

import { HOW_IT_WORKS_ASSETS_PREFIX } from '../constants'

import { HowToStepItemModel } from '../types'

type Props = {
  title: string
  showTopTear?: boolean
  steps: Array<HowToStepItemModel>
  buttonTitle: string
  buttonUrl: string
}

const HowItWorksSteps = ({ title, showTopTear, steps, buttonTitle, buttonUrl }: Props) => {
  const translate = useTranslate('how_it_works')
  const asset = useAsset(HOW_IT_WORKS_ASSETS_PREFIX)

  const renderStep = ({ image, titleKey, descriptionKey, learnMoreEntry }: HowToStepItemModel) => (
    <div key={titleKey} className="grid4 u-ui-padding-bottom-x4-large">
      <div className="how-it-works__steps-image-container u-ui-padding-bottom-x2-large@desktops u-ui-padding-bottom-x-large@portables">
        <Image
          src={asset(`portables/${image}`)}
          styling={Image.Styling.Rounded}
          srcset={`${asset(`desktops/${image}`)} 296w,
                   ${asset(`desktops@2x/${image}`)} 592w,
                   ${asset(`portables/${image}`)} 888w,
                   ${asset(`portables@2x/${image}`)} 1701w`}
          sizes="(min-width: 960px) 296px, (min-width: 721px) calc(100vw - 72px), calc(100vw - 48px)"
        />
      </div>
      <div className="u-padding-right-medium">
        <Text as="h1" text={translate(titleKey)} type={Text.Type.Heading} />
        <Spacer size={Spacer.Size.Medium} />
        <Text as="span" text={translate(descriptionKey)} theme="muted" />
        <Spacer size={Spacer.Size.Large} />
        <HelpCenterFaqEntryUrl
          type={learnMoreEntry}
          accessChannel={AccessChannel.ProductLink}
          render={url => (
            <a href={url} className="how-it-works__link" target="_blank" rel="noreferrer">
              {translate('learn_more')}
            </a>
          )}
        />
      </div>
    </div>
  )

  return (
    <div className="content-block">
      <div
        className={showTopTear ? 'tear tear--top u-background-greyscale-level-6 u-zindex-bump' : ''}
      >
        <div className="container">
          <div className="content-block__content u-ui-padding-bottom-x4-large u-ui-margin-horizontal-large">
            <div className="u-clearfix">
              <div className="content-block__heading u-ui-padding-vertical-x2-large@desktops u-ui-padding-top-x2-large@portables u-ui-padding-bottom-x2-large@portables">
                <Text as="h1" text={title} type={Text.Type.Heading} />
              </div>
              <div className="content-block__blocks">
                <div className="row">
                  <div className="grid12">
                    <div className="row">{steps.map(renderStep)}</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="u-text-center">
              <Button text={buttonTitle} url={buttonUrl} inline />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default HowItWorksSteps
